import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, Container, Spinner, Alert } from 'react-bootstrap';
import { formatDate } from '../utils/dataUtils';
import { apiService } from '../services/api';

const PageStats = () => {
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const fetchStats = async () => {
      try {
        setLoading(true);
        const stats = await apiService.fetchDashboardStats();
        if (mounted) {
          setDashboardStats({
            categories: stats.categories,
            totalBrands: stats.totalbrands,
            totalAccounts: stats.totalaccounts,
            firstDataFetched: new Date(stats.first_datafetchedinduration),
            lastDataFetched: new Date(stats.last_datafetchedinduration),
            totalPosts: stats.brandpost,
            totalComments: stats.comments,
            validatedAccounts: stats.validated_accounts || 0
          });
        }
      } catch (err) {
        if (mounted) {
          setError('Failed to load dashboard statistics');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <Container className="px-4 text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="px-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (!dashboardStats) {
    return null;
  }

  return (
    <Container className="px-4">
      <Card className="page-stats">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5>Dashboard Overview</h5>
          <div className="data-duration d-flex gap-2">
            <Badge bg="secondary" className="collection-badge">
              <i className="fas fa-calendar-alt me-1"></i>
              {formatDate(dashboardStats.firstDataFetched)}
              <i className="fas fa-arrow-right mx-2"></i>
              {formatDate(dashboardStats.lastDataFetched)}
            </Badge>
          </div>
        </Card.Header>
        <Card.Body className="py-4">
          <Row className="gx-4">
            <Col>
              <div className="stat-block d-flex align-items-center">
                <div className="stat-icon me-3">
                  <i className="fas fa-layer-group fa-2x text-primary"></i>
                </div>
                <div>
                  <div className="stat-label">Categories</div>
                  <div className="stat-value">{dashboardStats.categories}</div>
                </div>
              </div>
            </Col>
            <Col>
              <div className="stat-block d-flex align-items-center">
                <div className="stat-icon me-3">
                  <i className="fas fa-building fa-2x text-success"></i>
                </div>
                <div>
                  <div className="stat-label">Brands</div>
                  <div className="stat-value">{dashboardStats.totalBrands}</div>
                </div>
              </div>
            </Col>
            <Col>
              <div className="stat-block d-flex align-items-center">
                <div className="stat-icon me-3">
                  <i className="fas fa-users fa-2x text-info"></i>
                </div>
                <div>
                  <div className="stat-label">Accounts</div>
                  <div className="stat-value">
                    {dashboardStats.totalAccounts}
                    {dashboardStats.validatedAccounts > 0 && (
                      <span className="validated-count">
                        <Badge bg="success" pill className="ms-2" title="Validated accounts">
                          <i className="fas fa-check-circle me-1"></i>
                          {dashboardStats.validatedAccounts}
                        </Badge>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Col>
            <Col>
              <div className="stat-block d-flex align-items-center">
                <div className="stat-icon me-3">
                  <i className="fas fa-newspaper fa-2x text-warning"></i>
                </div>
                <div>
                  <div className="stat-label">Posts</div>
                  <div className="stat-value">{dashboardStats.totalPosts.toLocaleString()}</div>
                </div>
              </div>
            </Col>
            <Col>
              <div className="stat-block d-flex align-items-center">
                <div className="stat-icon me-3">
                  <i className="fas fa-comments fa-2x text-danger"></i>
                </div>
                <div>
                  <div className="stat-label">Comments</div>
                  <div className="stat-value">{dashboardStats.totalComments.toLocaleString()}</div>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PageStats;
